import React, { useState } from "react";
import { useIntl } from "react-intl";
import { CreateMember, Role } from "@/model";
import { useCreateMember } from "@/query/member/useCreateMember";
import { useAgentId, useModal } from "@/hook";
import Button from "@/component/Button";
import { Modal } from "@/component/modal";
import { TextInputGroup } from "@/component/input";
import { errorToast, successToast } from "@/component/Toast";

import RoleDropDown from "./RoleDropDown";


export const MEMBER_ADD_MODAL_ID = "member-add-modal-id";

const MemberAddModal: React.FC = () => {
    const intl = useIntl();
    const modal = useModal();
    const agentId = useAgentId();

    const [username, setUsername] = useState<string>("");
    const [role, setRole] = useState<Role>();
    const [validated, setValidated] = useState<boolean>(false);

    const { mutateAsync: createMember, isPending: isCreating } = useCreateMember(agentId);

    const validate = (form: any): form is CreateMember => {
        if (!form.username) {
            return false;
        } else if (!(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username))){
            errorToast(intl.formatMessage({ id: "i12025" }));
            return false;
        }
        if (!form.role_id) {
            return false;
        }
        return true;
    };

    const onClickConfirm = async () => {
        const form = {
            username: username,
            role_id: role?.id,
        };

        setValidated(true);
        if (!validate(form)) {
            return;
        }

        await createMember(form)
            .then(() => {
                successToast(intl.formatMessage({ id: "i12013" }, { username }));
            })
            .catch(() => {
                errorToast(intl.formatMessage({ id: "t002" }));
            })
            .finally(() => {
                modal.close(MEMBER_ADD_MODAL_ID);
            });
    };

    const onClose = () => {
        setUsername("");
        setRole(undefined);
        setValidated(false);
    };

    return (
        <Modal id={MEMBER_ADD_MODAL_ID}
               size="sm"
               title={intl.formatMessage({ id: "i12029" })}
               onClose={onClose}
        >
            <div className="modal-body">
                <div className="modal-description flex flex-col gap-4">
                    <TextInputGroup defaultValue=""
                                    label={intl.formatMessage({ id: "i12030" })}
                                    placeholder={intl.formatMessage({ id: "i12010" })}
                                    invalid={validated && !username}
                                    onChange={setUsername}
                    />
                    <div className="flex flex-col gap-1.5">
                        <div className="input-group-heading">
                            {intl.formatMessage({ id: "i12024" })}
                        </div>
                        <RoleDropDown onChange={setRole} />
                    </div>
                </div>
                <div className="modal-btn-group">
                    <Button size="sm"
                            variant="secondary"
                            onClick={() => modal.close(MEMBER_ADD_MODAL_ID)}
                    >
                        {intl.formatMessage({ id: "b0002" })}
                    </Button>
                    <Button size="sm"
                            variant="primary"
                            disabled={isCreating}
                            onClick={onClickConfirm}
                    >
                        {intl.formatMessage({ id: "b0001" })}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default MemberAddModal;